import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError, filter, switchMap, take} from 'rxjs/operators';
import {AuthenticationService} from "./authentication.service";
import {environment} from "../../../environments/environment";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private authenticationService: AuthenticationService) {
  }

  private readonly authEndpointsExcludedFrom401Handling = ['auth/login', 'auth/refresh', 'auth/verify'];

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isApiRequest = req.url.startsWith(environment.BASE_URL);
    const isAuthEndpoint = this.authEndpointsExcludedFrom401Handling.some(path => req.url.includes(path));
    const currentUser = this.authenticationService.currentUserValue;
    if (isApiRequest && !isAuthEndpoint && currentUser && currentUser.token) {
      req = this.addToken(req, currentUser.token);
    }

    return next.handle(req).pipe(
      catchError(error => {
        if (isApiRequest && !isAuthEndpoint && error instanceof HttpErrorResponse) {
          if (error.status === 401) {
            return this.handle401Error(req, next);
          }
        }
        return throwError(error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        this.isRefreshing = false;
        this.authenticationService.logout('Your session has expired. Please log in again.');
        return throwError(() => new Error('No refresh token found'));
      }

      this.refreshTokenSubject.next(refreshToken);

      return this.authenticationService.refreshToken().pipe(
        switchMap((tokenData: any) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(tokenData.token);

          return next.handle(this.addToken(request, tokenData.token));
        }),
        catchError(error => {
          // authenticationService.refreshToken() already logs out (with a
          // session-expired message) when the refresh call itself fails, so
          // this just resets local state and forwards the error.
          this.isRefreshing = false;
          return throwError(() => error);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => next.handle(this.addToken(request, token)))
      );
    }
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
}
